import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Phone, 
  Calendar, 
  FileText, 
  Brain, 
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Play,
  Pause,
  User,
  TrendingUp,
  Star,
  Clock
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';

interface CandidateScreeningInfoProps {
  candidateId: string;
}

interface ScreeningData {
  screening_session_id?: string;
  call_recording_id?: string;
  screening_transcript?: string;
  screening_date?: string;
  screening_score?: number;
  screening_summary?: any;
  voice_session?: {
    id: string;
    session_status: string;
    duration_seconds: number;
    audio_url?: string;
    ai_summary?: any;
    created_at: string;
  };
  call_recording?: {
    id: string;
    recording_url: string;
    phone_number?: string;
    ai_summary?: any;
    created_at: string;
  };
}

export const CandidateScreeningInfo: React.FC<CandidateScreeningInfoProps> = ({ candidateId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const { data: screeningData, isLoading } = useQuery({
    queryKey: ['candidate-screening', candidateId],
    queryFn: async () => {
      const { data: candidate, error } = await supabase
        .from('candidates')
        .select(`
          screening_session_id,
          call_recording_id,
          screening_transcript,
          screening_date,
          screening_score,
          screening_summary
        `)
        .eq('id', candidateId)
        .single();

      if (error) throw error;

      let voiceSession = null;
      let callRecording = null;

      // Fetch voice session data if available
      if (candidate.screening_session_id) {
        const { data: voiceData } = await supabase
          .from('voice_screening_sessions')
          .select(`
            id,
            session_status,
            duration_seconds,
            audio_url,
            ai_summary,
            created_at
          `)
          .eq('id', candidate.screening_session_id)
          .single();
        
        voiceSession = voiceData;
      }

      // Fetch call recording data if available
      if (candidate.call_recording_id) {
        const { data: callData } = await supabase
          .from('call_recordings')
          .select(`
            id,
            recording_url,
            phone_number,
            ai_summary,
            created_at
          `)
          .eq('id', candidate.call_recording_id)
          .single();
        
        callRecording = callData;
      }

      return {
        ...candidate,
        voice_session: voiceSession,
        call_recording: callRecording
      } as ScreeningData;
    },
    enabled: !!candidateId
  });

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getAISummary = () => {
    return screeningData?.voice_session?.ai_summary || 
           screeningData?.call_recording?.ai_summary || 
           screeningData?.screening_summary;
  };

  const getScreeningLink = () => {
    if (screeningData?.screening_session_id) {
      return `/screening/${screeningData.screening_session_id}`;
    }
    if (screeningData?.call_recording_id) {
      return `/screening/${screeningData.call_recording_id}`;
    }
    return null;
  };

  const getAudioUrl = () => {
    return screeningData?.voice_session?.audio_url || 
           screeningData?.call_recording?.recording_url;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">Loading screening information...</div>
        </CardContent>
      </Card>
    );
  }

  if (!screeningData || (!screeningData.screening_session_id && !screeningData.call_recording_id && !screeningData.screening_transcript && !screeningData.screening_summary)) {
    return null; // No screening data available
  }

  const aiSummary = getAISummary();
  const screeningLink = getScreeningLink();
  const audioUrl = getAudioUrl();

  return (
    <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Phone className="h-5 w-5 text-blue-600" />
            Screening Information
          </CardTitle>
          <div className="flex items-center gap-2">
            {screeningLink && (
              <Button variant="outline" size="sm" asChild>
                <Link to={screeningLink} className="flex items-center gap-1">
                  <ExternalLink className="h-4 w-4" />
                  View Full Details
                </Link>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Basic Screening Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-blue-600" />
            <div>
              <div className="text-sm font-medium">Screening Date</div>
              <div className="text-sm text-muted-foreground">
                {screeningData.screening_date 
                  ? format(new Date(screeningData.screening_date), 'MMM d, yyyy')
                  : 'Not recorded'
                }
              </div>
            </div>
          </div>

          {screeningData.voice_session?.duration_seconds && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-600" />
              <div>
                <div className="text-sm font-medium">Duration</div>
                <div className="text-sm text-muted-foreground">
                  {formatDuration(screeningData.voice_session.duration_seconds)}
                </div>
              </div>
            </div>
          )}

          {screeningData.screening_score && (
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              <div>
                <div className="text-sm font-medium">Score</div>
                <Badge variant={screeningData.screening_score >= 80 ? "default" : "secondary"}>
                  {screeningData.screening_score}/100
                </Badge>
              </div>
            </div>
          )}
        </div>

        {/* Audio Player */}
        {audioUrl && (
          <div className="bg-white rounded-lg p-4 border">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsPlaying(!isPlaying)}
                className="flex items-center gap-2"
              >
                {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                {isPlaying ? 'Pause' : 'Play'} Recording
              </Button>
              <div className="text-sm text-muted-foreground">
                Call recording available
              </div>
            </div>
            <audio 
              src={audioUrl}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              controls 
              className="w-full mt-2 h-8"
            />
          </div>
        )}

        {isExpanded && (
          <>
            <Separator />
            
            {/* AI Summary Preview */}
            {aiSummary && (
              <div className="bg-white rounded-lg p-4 border">
                <div className="flex items-center gap-2 mb-3">
                  <Brain className="h-5 w-5 text-blue-600" />
                  <h4 className="font-semibold text-blue-900">AI Analysis Summary</h4>
                </div>
                
                {aiSummary.candidate_summary && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm font-medium text-gray-600">Experience Level</div>
                        <Badge variant="secondary">{aiSummary.candidate_summary.experience_level}</Badge>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-600">Years Experience</div>
                        <div className="text-sm">{aiSummary.candidate_summary.years_experience}</div>
                      </div>
                    </div>

                    {aiSummary.candidate_summary.primary_skills?.length > 0 && (
                      <div>
                        <div className="text-sm font-medium text-gray-600 mb-2">Primary Skills</div>
                        <div className="flex flex-wrap gap-1">
                          {aiSummary.candidate_summary.primary_skills.map((skill: string, idx: number) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {aiSummary.screening_assessment && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-sm font-medium text-gray-600">Communication</div>
                          <Badge variant="outline">{aiSummary.screening_assessment.communication_quality}</Badge>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-600">Technical</div>
                          <Badge variant="outline">{aiSummary.screening_assessment.technical_competency}</Badge>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Transcript Preview */}
            {screeningData.screening_transcript && (
              <div className="bg-white rounded-lg p-4 border">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <h4 className="font-semibold text-blue-900">Transcript Preview</h4>
                </div>
                <div className="text-sm text-gray-700 max-h-32 overflow-y-auto">
                  {screeningData.screening_transcript.substring(0, 300)}
                  {screeningData.screening_transcript.length > 300 && '...'}
                </div>
                {screeningLink && (
                  <Button variant="link" size="sm" asChild className="mt-2 p-0 text-blue-600">
                    <Link to={screeningLink}>View full transcript →</Link>
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};